/**
 * Owner Review Links — per-branch table with inline edit + analytics.
 *
 * Frequency rule editor supports:
 *   - every_n_visits  (n)
 *   - after_n_stamps  (n)
 *   - after_redemption (no n)
 * with optional min_days_between_prompts.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { Save, Star } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, EmptyState, StatusPill,
} from '@/components/ui_primitives/Misc';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui_primitives/Table';
import Button from '@/components/ui_primitives/Button';
import { Input, Select } from '@/components/ui_primitives/Field';

const RULE_OPTIONS = [
  { value: 'every_n_visits', label: 'Every N visits' },
  { value: 'after_n_stamps', label: 'After N stamps' },
  { value: 'after_redemption', label: 'After each redemption' },
];

function conversionColor(rate) {
  if (rate >= 0.25) return 'green';
  if (rate >= 0.1) return 'amber';
  return 'red';
}

export default function OwnerReviewsPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);
  const qc = useQueryClient();

  const linksQ = useQuery({
    queryKey: qk.ownerReviewLinks(brandId),
    queryFn: () => api.ownerReviewLinks(token),
    enabled: !!token && !!brandId,
  });
  const analyticsQ = useQuery({
    queryKey: qk.ownerReviewAnalytics(brandId, '30d'),
    queryFn: () => api.ownerReviewAnalytics(token, '30d'),
    enabled: !!token && !!brandId,
  });
  const branchesQ = useQuery({
    queryKey: qk.ownerBranches(brandId),
    queryFn: () => api.ownerBranches(token),
    enabled: !!token && !!brandId,
  });

  const save = useMutation({
    mutationFn: ({ branchId, body }) => api.ownerPatchReviewLink(token, branchId, body),
    onSuccess: () => {
      toast.success('Review link saved');
      qc.invalidateQueries({ queryKey: qk.ownerReviewLinks(brandId) });
      qc.invalidateQueries({ queryKey: qk.ownerReviewAnalytics(brandId, '30d') });
    },
    onError: (e) => toast.error(String(e?.detail ?? 'Save failed')),
  });

  if (linksQ.isLoading) return <FullSpinner />;
  if (linksQ.error) return <QueryError error={linksQ.error} onRetry={() => linksQ.refetch()} />;

  const links = linksQ.data || [];
  const branches = (branchesQ.data || []).filter((b) => b.is_active !== false);
  const linksByBranch = new Map(links.map((l) => [l.branch_id, l]));
  const rows = branches.map((b) => {
    const l = linksByBranch.get(b.id) || {
      branch_id: b.id, branch_name: b.name, url: '',
      frequency_rule: { type: 'every_n_visits', n: 3, min_days_between_prompts: 7 },
      prompt_views: 0, clicks: 0, dismissals: 0,
    };
    return { ...l, branch_name: b.name };
  });

  const totals = analyticsQ.data?.totals;
  const perBranch = analyticsQ.data?.per_branch || [];

  return (
    <div className="space-y-6" data-testid="owner-reviews-page">
      <PageHeader
        kicker="Review Links"
        title="Google Review prompts"
        subtitle="Configure when each branch asks customers for a Google Review and track conversion."
      />

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="reviews-totals">
        <SectionCard padded>
          <div className="text-[11px] uppercase tracking-widest text-white/50">Prompts shown</div>
          <div className="text-2xl font-display font-bold mt-2">{(totals?.prompt_views ?? 0).toLocaleString('id-ID')}</div>
        </SectionCard>
        <SectionCard padded>
          <div className="text-[11px] uppercase tracking-widest text-white/50">Clicks to Google</div>
          <div className="text-2xl font-display font-bold mt-2 text-gold">{(totals?.clicks ?? 0).toLocaleString('id-ID')}</div>
        </SectionCard>
        <SectionCard padded>
          <div className="text-[11px] uppercase tracking-widest text-white/50">Dismissals</div>
          <div className="text-2xl font-display font-bold mt-2">{(totals?.dismissals ?? 0).toLocaleString('id-ID')}</div>
        </SectionCard>
        <SectionCard padded>
          <div className="text-[11px] uppercase tracking-widest text-white/50">Conversion</div>
          <div className="text-2xl font-display font-bold mt-2">{((totals?.conversion_rate ?? 0) * 100).toFixed(1)}%</div>
        </SectionCard>
      </div>

      <SectionCard testID="reviews-chart">
        <h3 className="font-display text-lg font-semibold mb-1">Views vs clicks by branch</h3>
        <p className="text-xs text-white/50 mb-4">All-time totals per branch.</p>
        {perBranch.length === 0 ? (
          <div className="text-sm text-white/40 py-12 text-center">No review prompt activity yet.</div>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={perBranch} margin={{ left: -10, right: 16, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="branch_name" tick={{ fill: '#8A8A8A', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#8A8A8A', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#0E0E13', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="prompt_views" stroke="#8A8A8A" strokeWidth={2} dot={false} name="Views" isAnimationActive={false} />
                <Line type="monotone" dataKey="clicks" stroke="#C9A84C" strokeWidth={2} dot={false} name="Clicks" isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </SectionCard>

      {rows.length === 0 ? (
        <EmptyState
          icon={<Star size={20} />}
          title="No branches"
          subtitle="Add branches first; each branch gets its own review-link configuration."
        />
      ) : (
        <SectionCard padded={false}>
          <Table testID="reviews-table">
            <THead>
              <Tr>
                <Th>Branch</Th>
                <Th>Google Review URL</Th>
                <Th>Frequency rule</Th>
                <Th>Views</Th>
                <Th>Clicks</Th>
                <Th>Conversion</Th>
                <Th align="right">Action</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((row) => (
                <ReviewRow key={row.branch_id} row={row} onSave={save.mutate} pending={save.isPending} />
              ))}
            </TBody>
          </Table>
        </SectionCard>
      )}
    </div>
  );
}

function ReviewRow({ row, onSave, pending }) {
  const [url, setUrl] = useState(row.url ?? '');
  const [type, setType] = useState(row.frequency_rule?.type ?? 'every_n_visits');
  const [n, setN] = useState(row.frequency_rule?.n ?? 3);
  const [minDays, setMinDays] = useState(row.frequency_rule?.min_days_between_prompts ?? 7);
  const rate = row.prompt_views > 0 ? row.clicks / row.prompt_views : 0;

  return (
    <Tr>
      <Td><div className="font-medium">{row.branch_name}</div></Td>
      <Td>
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://g.page/r/..."
          className="min-w-[200px]"
          testID={`review-url-${row.branch_id}`}
        />
      </Td>
      <Td>
        <div className="flex items-center gap-2">
          <Select value={type} onChange={(e) => setType(e.target.value)} className="w-44">
            {RULE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          {type !== 'after_redemption' ? (
            <Input type="number" min={1} value={n} onChange={(e) => setN(parseInt(e.target.value || '1', 10))} className="w-20" />
          ) : null}
          <Input type="number" min={0} value={minDays} onChange={(e) => setMinDays(parseInt(e.target.value || '0', 10))} className="w-20" title="Min days between prompts" />
        </div>
      </Td>
      <Td>{(row.prompt_views ?? 0).toLocaleString('id-ID')}</Td>
      <Td className="text-gold font-semibold">{(row.clicks ?? 0).toLocaleString('id-ID')}</Td>
      <Td>
        <StatusPill variant={conversionColor(rate)}>{(rate * 100).toFixed(1)}%</StatusPill>
      </Td>
      <Td align="right">
        <Button
          size="sm"
          leftIcon={<Save size={12} />}
          loading={pending}
          onClick={() => {
            const rule = { type, min_days_between_prompts: minDays };
            if (type !== 'after_redemption') rule.n = n;
            onSave({ branchId: row.branch_id, body: { url, frequency_rule: rule } });
          }}
          testID={`review-save-${row.branch_id}`}
        >
          Save
        </Button>
      </Td>
    </Tr>
  );
}
